import * as React from 'react'
import { NewModal, Select, NewButton, StepType, StepTypeNames, StepConditionType, ConfirmAction, NewTabs } from 'prace-common-components'
import Grid from '@material-ui/core/Grid'
import { $ModalContainer, $SelectModalContainer, $ButtonContainer, $TabPanel } from './styles'
import { StepTypeEntry, StepTypeModalProps } from './types'
import { AllocationDecisionOptions } from './AllocationDecisionOptions'
import { FinalReportOptions } from './FinalReportOptions'

const STEP_TYPE_ITEMS = Object.values(StepType).map((value) => ({
  value,
  label: StepTypeNames[value],
}))

const emptyEntry = (stepType: StepType): StepTypeEntry => ({
  stepType,
  elements: [],
  condition: null,
  value: '',
})

const REVIEW_TYPES = [StepType.technical_review, StepType.scientific_review]

const isEntryInvalid = (entry: StepTypeEntry): boolean => {
  if (entry.stepType === StepType.final_report) return entry.elements.length !== 1
  /* Only the allocation step's decision rule is validated. The review types carry one too, but Reviewed is granted
     by any completed review whatever it holds */
  if (entry.stepType !== StepType.allocation_step) return false
  const { elements, condition, value } = entry
  if (!elements.length || !condition) return true
  const nonValueType = condition === StepConditionType.empty || condition === StepConditionType.nonEmpty
  return !nonValueType && !value
}

export const StepTypeModal: React.FC<StepTypeModalProps> = (
  { step, open, onClose, onSave, loading, takenStepTypes = [] },
) => {
  const rawConfigs = (step.config as any)?.stepTypeConfigs ?? []
  const [entries, setEntries] = React.useState<StepTypeEntry[]>(() =>
    rawConfigs.map((c: any) => ({
      id: c.id,
      stepType: c.stepType,
      elements: Array.isArray(c.elements)
        ? c.elements.map((e: number | { id: number }) =>
          typeof e === 'number' ? { id: e } : e,
        )
        : [],
      condition: c.condition ?? null,
      value: c.value ?? '',
    })),
  )
  const [activeTab, setActiveTab] = React.useState(0)
  const [addModalOpen, setAddModalOpen] = React.useState(false)
  const [newStepType, setNewStepType] = React.useState<StepType | ''>('')

  const usedStepTypes = entries.map((e) => e.stepType)
  const availableItems = STEP_TYPE_ITEMS.filter(
    (item) => !usedStepTypes.includes(item.value) &&
      !takenStepTypes.includes(item.value) &&
      (item.value !== StepType.project_application || step.order === 0),
  )

  const handleOpenAddModal = () => {
    setNewStepType('')
    setAddModalOpen(true)
  }

  const handleAddStepType = () => {
    if (!newStepType) return
    const newEntries = [...entries, emptyEntry(newStepType as StepType)]
    setEntries(newEntries)
    setActiveTab(newEntries.length - 1)
    setAddModalOpen(false)
    setNewStepType('')
  }

  const handleRemove = (idx: number) => {
    const newEntries = entries.filter((_, i) => i !== idx)
    setEntries(newEntries)
    setActiveTab((prev) => Math.min(prev, Math.max(0, newEntries.length - 1)))
  }

  const handleEntryChange = (idx: number, updated: StepTypeEntry) => {
    setEntries((prev) => prev.map((e, i) => i === idx ? updated : e))
  }

  const isSaveDisabled = entries.some(isEntryInvalid)
  const tabs = entries.map((entry, idx) => ({ id: idx, label: StepTypeNames[entry.stepType] }))
  const activeEntry = entries.length > 0 ? entries[activeTab] ?? null : null

  return (
    <>
      <NewModal open={open} onClose={onClose} title='Step Type Configuration' alternateTitle>
        <$ModalContainer>
          <Grid container justifyContent='flex-start' style={{ marginBottom: 16 }}>
            <NewButton creator variant='outlined' disabled={availableItems.length === 0} onClick={handleOpenAddModal}>
              Add Step Type
            </NewButton>
          </Grid>

          {entries.length > 0 && (
            <>
              <NewTabs
                value={activeTab}
                onChange={setActiveTab}
                tabs={tabs}
                scrollable={tabs.length > 3}
              />
              {entries.length > 0 && activeEntry && (
                <$TabPanel>
                  {activeEntry.stepType === StepType.allocation_step ? (
                    <AllocationDecisionOptions
                      step={step}
                      config={activeEntry}
                      onChange={(updated) => handleEntryChange(activeTab, updated)}
                    />
                  ) : REVIEW_TYPES.includes(activeEntry.stepType) ? (
                    <AllocationDecisionOptions
                      step={step}
                      config={activeEntry}
                      outcomeLabel='approved'
                      optional
                      onChange={(updated) => handleEntryChange(activeTab, updated)}
                    />
                  ) : activeEntry.stepType === StepType.final_report ? (
                    <FinalReportOptions
                      step={step}
                      config={activeEntry}
                      onChange={(updated) => handleEntryChange(activeTab, updated)}
                    />
                  ) : (
                    <Grid container justifyContent='center' style={{ padding: '16px 0', color: '#757778' }}>
                      No additional configuration required for this step type.
                    </Grid>
                  )}
                  <Grid container justifyContent='flex-end' style={{ marginTop: 12 }}>
                    <ConfirmAction
                      deletion
                      title='Remove Step Type'
                      description={`Remove "${StepTypeNames[activeEntry.stepType]}" from this step?`}
                      onConfirm={() => handleRemove(activeTab)}
                    >
                      <NewButton error variant='outlined'>Remove Step Type</NewButton>
                    </ConfirmAction>
                  </Grid>
                </$TabPanel>
              )}
            </>
          )}

          <$ButtonContainer container justifyContent='flex-end'>
            <NewButton loading={loading} disabled={isSaveDisabled} onClick={() => onSave(entries)}>
              Save
            </NewButton>
          </$ButtonContainer>
        </$ModalContainer>
      </NewModal>

      <NewModal open={addModalOpen} onClose={() => setAddModalOpen(false)} title='Add Step Type' small alternateTitle>
        <$SelectModalContainer>
          <Select
            name='newStepType'
            title='Step Type'
            value={newStepType}
            items={availableItems}
            placeholder='Select a step type'
            onChange={(_: string, value: Nullable<StringNumber>) => setNewStepType(value as StepType)}
          />
          <$ButtonContainer container justifyContent='flex-end'>
            <NewButton disabled={!newStepType} onClick={handleAddStepType}>
              Confirm
            </NewButton>
          </$ButtonContainer>
        </$SelectModalContainer>
      </NewModal>
    </>
  )
}
